import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Plus, X, Star } from 'lucide-react';
import ImageCropDialog from './ImageCropDialog';

const ALLOWED = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_IMAGES = 5;

export const ProductImagesManager = ({ images, onChange, api }) => {
  const inputRef = useRef(null);
  const [cropSrc, setCropSrc] = useState(null);

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ALLOWED.includes(file.type)) return toast.error('Use JPEG, PNG ou WebP');
    if (file.size > 10 * 1024 * 1024) return toast.error('Imagem muito grande (máx. 10MB)');
    setCropSrc(URL.createObjectURL(file));
  };

  const closeCrop = () => {
    URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  };

  const uploadCropped = async (blob) => {
    const formData = new FormData();
    formData.append('file', new File([blob], 'produto.jpg', { type: 'image/jpeg' }));
    try {
      const res = await api().post('/upload/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange([...images, res.data.url]);
      toast.success('Imagem carregada');
      closeCrop();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha no upload da imagem');
    }
  };

  const remove = (i) => onChange(images.filter((_, idx) => idx !== i));
  const makeMain = (i) => onChange([images[i], ...images.filter((_, idx) => idx !== i)]);

  return (
    <div data-testid="product-images-manager">
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        {images.map((url, i) => (
          <div key={url} className={`relative aspect-square rounded-lg overflow-hidden bg-white border-2 ${i === 0 ? 'border-sonangol-orange' : 'border-zinc-700'}`}
            data-testid={`product-image-thumb-${i}`}>
            <img src={url} alt={`Imagem ${i + 1}`} className="w-full h-full object-cover" />
            {i === 0 ? (
              <span className="absolute bottom-0 inset-x-0 bg-sonangol-orange text-white text-[10px] text-center py-0.5">Principal</span>
            ) : (
              <button type="button" onClick={() => makeMain(i)} title="Definir como principal"
                className="absolute bottom-1 left-1 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center"
                data-testid={`product-image-main-${i}`}>
                <Star className="w-3.5 h-3.5" />
              </button>
            )}
            <button type="button" onClick={() => remove(i)} title="Remover"
              className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-red-600"
              data-testid={`product-image-remove-${i}`}>
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        {images.length < MAX_IMAGES && (
          <button type="button" onClick={() => inputRef.current?.click()}
            className="aspect-square rounded-lg border-2 border-dashed border-zinc-600 text-zinc-400 hover:border-sonangol-orange hover:text-sonangol-orange flex flex-col items-center justify-center gap-1 transition-colors"
            data-testid="product-image-add-btn">
            <Plus className="w-6 h-6" />
            <span className="text-xs">Adicionar</span>
          </button>
        )}
      </div>
      <p className="text-xs text-zinc-500 mt-2">{images.length}/{MAX_IMAGES} imagens · a primeira é a principal · JPEG, PNG ou WebP</p>
      <input ref={inputRef} type="file" accept={ALLOWED.join(',')} onChange={pickFile} className="hidden" data-testid="product-image-input" />
      {cropSrc && <ImageCropDialog src={cropSrc} onCancel={closeCrop} onConfirm={uploadCropped} />}
    </div>
  );
};

export default ProductImagesManager;
